
<template>
    <div :class="['details-card', {'details-card--suggestion': isSuggestion}]"
        @click="onPress"
    >
        <div class="details-card__indicator"
            :style="{background: color}"
        />
        <div class="details-card__icon">
            <img :src="`../../../assets/images/wallet/${icon}.svg`"
                alt="details card icon"
            >
        </div>
        <div :class="['details-card__body', {'details-card__body--suggestion': isSuggestion}]">
            <p v-if="category">
                {{ category }}
            </p>
            <p class="strong">{{ title }}</p>
            <p v-if="dateSinceLocked !== '0'"
                class="small"
            >
                {{ formattedDateSinceLocked }}
            </p>
            <p v-else-if="subtitle"
                :class="{'small': !isSuggestion}"
            >
                <translation-string :translation="subtitle" />
            </p>
        </div>
        <div v-if="amount"
            class="details-card__right"
        >
            <div v-if="unlockPercentage > 0"
                class="details-card__progress"
            >
                <div class="progress-bar"
                    :style="progressBarStyle"
                >
                    <div v-if="formattedDateToUnlock.length"
                        class="progress-bar__label"
                    >
                        <span class="bold">{{ formattedDateToUnlock[1] }}</span>
                        <span>{{ formattedDateToUnlock[0] }}</span>
                    </div>
                </div>
            </div>
            <div class="details-card__amount strong">
                {{ Math.round(amount) }} {{ currency.shortCode }}
            </div>
        </div>
        <img v-else
            class="details-card__arrow"
            src="@webapp/assets/images/wallet/arrow.svg"
            alt="arrow icon"
        >
    </div>
</template>

<script>
// Utils
import {
    format,
} from '@shared/utils/dates.js';

// Components
import translationString from '@webapp/components/translation-string.vue';

export default {
    name: 'wallet-details-card',
    components: {
        translationString,
    },
    props: {
        title: {
            type: String,
            required: true,
        },
        subtitle: {
            type: String,
            required: false,
            default: '',
        },
        category: {
            type: String,
            required: false,
            default: '',
        },
        amount: {
            type: String,
            required: false,
            default: '',
        },
        icon: {
            type: String,
            required: true,
        },
        currency: {
            type: Object,
            required: false,
            default: () => ({}),
        },
        dateSinceLocked: {
            type: String,
            default: '0',
            required: false,
        },
        dateToUnlock: {
            type: String,
            default: '0',
            required: false,
        },
        color: {
            type: String,
            default: '#fff',
            required: false,
            validator(input) {
                return input.startsWith('#');
            },
        },
        onPress: {
            type: Function,
            required: false,
            default: () => ({}),
        },
        isSuggestion: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    computed: {
        unlockPercentage() {
            if (!this.dateSinceLocked || !this.dateToUnlock) {
                return 0;
            }

            const dateToUnlockInMs = new Date(this.dateToUnlock).getTime();
            const dateSinceLockedInMs = new Date(this.dateSinceLocked).getTime();

            const totalDiff = dateToUnlockInMs - dateSinceLockedInMs;
            const currentDiff = Date.now() - dateSinceLockedInMs;

            // In case the current diff is greater than the totalDiff, default to totalDiff and return 1,
            // It should not be less then 0.05 so that indicator color is visible
            return Math.max((Math.min(currentDiff, totalDiff) / totalDiff), 0.05);
        },
        // Returns an array containing day and month. Order depends on the user locale
        formattedDateToUnlock() {
            return format(this.dateToUnlock, 'dayShortMonthShort').split(' ');
        },

        formattedDateSinceLocked() {
            return `${format(this.dateSinceLocked, 'dayShortMonthShort')} - ${format(this.dateSinceLocked, 'HH:mm')}`;
        },

        formattedTokenAmount() {
            return `${Math.trunc(this.amount)} ${this.currency.shortCode}`;
        },

        progressBarStyle() {
            const elapsedInDegs = 360 * this.unlockPercentage;

            return {
                background: `conic-gradient(${this.color} ${elapsedInDegs}deg, white 0)`,
            };
        },
    },
};
</script>

<style lang="scss" scoped>
p {
    margin: 0;
}

.bold {
    font-weight: 700;
}

.strong {
    font-weight: 700;
    font-size: 1.5rem;
}

.small {
    font-size: 1.1rem;
    color: $blue-comment-text;
}

.details-card {
    position: relative;
    display: flex;
    min-height: 10rem;
    align-items: center;
    padding: 2.4rem 2.8rem 2.4rem 0;
    font-size: 1.3rem;
    background: $white-color;
    border-radius: 8px;
    box-sizing: border-box;
    margin-bottom: 1.2rem;

    @include max-width(lg) {
        padding: 1.3rem 2rem 1.3rem 0;
    }

    &--suggestion {
        cursor: pointer;
    }

    &__indicator {
        position: absolute;
        width: 0.5rem;
        height: calc(100% - 2.4rem);
        top: 1.2rem;
        left: 0;
        border-radius: 0 14px 14px 0;
    }

    &__icon {
        display: flex;
        width: 9rem;
        align-items: center;
        justify-content: center;
    }

    &__body {
        flex: 1;
        margin-right: 2rem;
        overflow: hidden;
        max-width: 38rem;

        p,
        p > * {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        &--suggestion {
            max-width: none;

            p,
            p > * {
                white-space: normal;
                overflow: auto;
                text-overflow: unset;
            }
        }
    }

    &__right {
        margin-left: auto;
        display: flex;
        align-items: center;

        @include max-width(lg) {
            flex-direction: column;
        }
    }

    &__amount {
        min-width: 7rem;
        text-align: right;
        margin-left: 0.8rem;

        @include max-width(lg) {
            margin-left: 0;
            margin-top: 0.6rem;
            min-width: auto;
        }
    }

    &__arrow {
        align-self: flex-start;
        margin-left: auto;

        @include max-width(lg) {
            margin-top: 1.1rem;
        }
    }
}

.progress-bar {
    position: relative;
    height: 4.4rem;
    width: 4.4rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: inset 1px 0 1px rgba(0, 33, 55, 0.15), inset -1px 0 2px rgba(0, 33, 55, 0.15);

    &:before {
        content: "";
        position: absolute;
        background: $white-color;
        height: 3.4rem;
        width: 3.4rem;
        border-radius: 50%;
        box-shadow: 0 0 3px rgba(0, 0, 0, 0.25);
    }

    &__label {
        z-index: 1;
        font-size: 1.1rem;
        line-height: 1.5rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-transform: uppercase;
    }
}
</style>